import { WALLBOARD_SLA_RISK_WINDOW_S } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Chip, Skeleton } from "@sagot/ui";
import type { ReactNode } from "react";
import { formatClock, tierLabel, tileTones, type TileTone, type WallboardSnapshot } from "./wallboard-model";

const TONE_CLASS: Readonly<Record<TileTone, string>> = {
  neutral: "border-line bg-surface",
  warning: "border-warning bg-warning-soft",
  danger: "border-danger bg-danger-soft",
};

interface TileProps {
  label: string;
  value: string;
  hint: string;
  tone?: TileTone;
  testId: string;
  className?: string;
  children?: ReactNode;
}

/** One live figure: a large number under its label, a hint, and - when it needs attention - an icon and words as well as the colour. */
function Tile({ label, value, hint, tone = "neutral", testId, className = "", children }: TileProps) {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby={`${testId}-label`}
      className={`flex min-w-0 flex-col rounded-card border p-4 shadow-card ${TONE_CLASS[tone]} ${className}`}
      data-testid={testId}
      data-tone={tone}
    >
      <h2 id={`${testId}-label`} className="text-xs font-semibold text-fg-muted">{label}</h2>
      <p className="mt-1 text-3xl font-extrabold leading-tight tracking-tight text-fg tabular-nums xl:text-4xl" data-testid={`${testId}-value`}>{value}</p>
      <p className="mt-1 text-xs text-fg-muted">{hint}</p>
      {tone !== "neutral" ? (
        <p className="mt-2">
          <Chip tone={tone === "danger" ? "danger" : "warning"} size="md" icon="alert-triangle">{t("wallboard.tiles.needsAttention")}</Chip>
        </p>
      ) : null}
      {children}
    </section>
  );
}

function Breakdown({ title, items, testId }: { title: string; items: readonly { key: string; label: string; count: number }[]; testId: string }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3 min-w-0" data-testid={testId}>
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{title}</h3>
      <ul className="mt-1 flex flex-wrap gap-1.5">
        {items.map((item) => (
          <li key={item.key}>
            <Chip tone="outline" size="md" className="max-w-full">
              <span className="truncate">{item.label}</span>
              <span className="ml-1.5 font-extrabold text-fg tabular-nums">{item.count}</span>
            </Chip>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TilesSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" role="status" aria-busy="true" aria-label={t("wallboard.board.loading")} data-testid="wallboard-tiles-loading">
      {[0, 1, 2, 3, 4, 5, 6].map((tile) => (
        <div key={tile} className={`rounded-card border border-line bg-surface p-4 shadow-card ${tile === 0 ? "col-span-2" : ""}`}>
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-9 w-20" />
          <Skeleton className="mt-3 h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

/** The live figures (mockup pin 1): queue with its split by team and tier, longest wait, priority waiting, agents online, at limit, SLAs at risk, safety cases. */
export function WallboardTiles({ tiles }: { tiles: WallboardSnapshot["tiles"] }) {
  const { t } = useTranslation();
  const tones = tileTones(tiles);
  const teams = tiles.queue.by_team.map((row) => ({ key: row.team_id ?? "none", label: row.team ?? t("wallboard.tiles.noTeam"), count: row.count }));
  const tiers = tiles.queue.by_tier.map((row) => ({ key: row.tier ?? "none", label: tierLabel(row.tier, t("wallboard.tiles.noTier")), count: row.count }));
  const minutes = Math.round(WALLBOARD_SLA_RISK_WINDOW_S / 60);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" role="group" aria-label={t("wallboard.tiles.group")} data-testid="wallboard-tiles">
      <Tile label={t("wallboard.tiles.queue")} value={String(tiles.queue.total)} hint={t("wallboard.tiles.queueHint")} testId="tile-queue" className="col-span-2">
        <Breakdown title={t("wallboard.tiles.breakdownTeams")} items={teams} testId="tile-queue-teams" />
        <Breakdown title={t("wallboard.tiles.breakdownTiers")} items={tiers} testId="tile-queue-tiers" />
      </Tile>
      <Tile
        label={t("wallboard.tiles.longestWait")}
        value={tiles.longest_wait_s === null ? "–" : formatClock(tiles.longest_wait_s)}
        hint={tiles.longest_wait_s === null ? t("wallboard.tiles.longestWaitNone") : t("wallboard.tiles.queueHint")}
        tone={tones.longestWait}
        testId="tile-longest-wait"
      />
      <Tile label={t("wallboard.tiles.priorityWaiting")} value={String(tiles.priority_waiting)} hint={t("wallboard.tiles.priorityWaitingHint")} tone={tones.priorityWaiting} testId="tile-priority" />
      <Tile
        label={t("wallboard.tiles.agentsOnline")}
        value={`${String(tiles.agents_online.online)} / ${String(tiles.agents_online.total)}`}
        hint={t("wallboard.tiles.agentsOnlineHint")}
        testId="tile-agents-online"
      />
      <Tile label={t("wallboard.tiles.atLimit")} value={String(tiles.at_limit)} hint={t("wallboard.tiles.atLimitHint")} tone={tones.atLimit} testId="tile-at-limit" />
      <Tile label={t("wallboard.tiles.slasAtRisk")} value={String(tiles.slas_at_risk)} hint={t("wallboard.tiles.slasAtRiskHint", { minutes })} tone={tones.slasAtRisk} testId="tile-slas" />
      <Tile label={t("wallboard.tiles.safetyCases")} value={String(tiles.safety_cases)} hint={t("wallboard.tiles.safetyCasesHint")} tone={tones.safetyCases} testId="tile-safety" />
    </div>
  );
}
